import { shortDate } from "../notifications/notificationVocabulary";
import type {
  ReviewState,
  BugSeverity,
  BugSource,
  ClientContactChannel,
  ClientContactDirection,
  HistoryEventType,
  Priority,
  WorkItemEvidenceKind,
  WorkItemStatus,
  WorkItemTransitionReason,
  WorkItemType,
} from "../../shared/types/api";
import type { Tone } from "../../shared/ui";

/**
 * How work-item vocabulary reads.
 *
 * <p>Kept in one place so a bug's severity looks the same on a list, a detail page and a history
 * entry. Two screens disagreeing about what "ready for QA" looks like is the sort of small
 * inconsistency that makes an application feel unfinished.
 */

export const TYPE_LABELS: Record<WorkItemType, string> = {
  EPIC: "Epic",
  USER_STORY: "Story",
  TECHNICAL_TASK: "Task",
  BUG: "Bug",
  SPIKE: "Spike",
  SUBTASK: "Subtask",
  ANALYSIS: "Analysis",
  DEMO_PREPARATION: "Demo prep",
  MIGRATION: "Migration",
  SERVER_WORK: "Server work",
  CLIENT_SERVICE: "Client service",
  MEETING: "Meeting",
};

/**
 * A short glyph per type.
 *
 * <p>Letters rather than icons: ten types need to be distinguishable at a glance in a dense
 * list, and ten different pictograms are harder to tell apart than ten letters — particularly
 * for anyone who has not learned the icon set yet.
 */
export const TYPE_GLYPH: Record<WorkItemType, string> = {
  EPIC: "E",
  USER_STORY: "S",
  TECHNICAL_TASK: "T",
  BUG: "B",
  SPIKE: "?",
  SUBTASK: "·",
  ANALYSIS: "A",
  DEMO_PREPARATION: "D",
  MIGRATION: "M",
  SERVER_WORK: "R",
  CLIENT_SERVICE: "C",
  MEETING: "M",
};

export const TYPE_TONE: Record<WorkItemType, Tone> = {
  EPIC: "violet",
  USER_STORY: "green",
  TECHNICAL_TASK: "blue",
  BUG: "red",
  SPIKE: "amber",
  SUBTASK: "gray",
  ANALYSIS: "blue",
  DEMO_PREPARATION: "violet",
  MIGRATION: "amber",
  SERVER_WORK: "blue",
  CLIENT_SERVICE: "green",
  MEETING: "gray",
};

/**
 * What each type (§15) has to produce before it counts as finished.
 *
 * <p>Total rather than Partial, and empty arrays rather than absent keys. This mirrors
 * `WorkItemType.completionEvidence()`, whose switch is exhaustive for the stated reason that
 * adding a kind should force the decision. A Partial map made the opposite true here: a fourth
 * simple type could be added on the server and compile clean on this side with no entry at all,
 * and the only symptom would be an evidence panel that renders nothing, no way to attach
 * anything, and Complete failing with a server error nobody could act on.
 *
 * <p>The six development types owe nothing, which is why they are listed with nothing.
 */
export const TYPE_EVIDENCE: Record<WorkItemType, WorkItemEvidenceKind[]> = {
  EPIC: [],
  USER_STORY: [],
  TECHNICAL_TASK: [],
  BUG: [],
  SPIKE: [],
  SUBTASK: [],
  ANALYSIS: ["DOCUMENT"],
  DEMO_PREPARATION: ["SCREENSHOT"],
  MIGRATION: ["QUERIES", "SCREENSHOT"],
  SERVER_WORK: ["SCREENSHOT"],
  /* Its record is the call, which is structured rather than a file — asking for a screenshot of
     a telephone conversation would be asking for nothing. See the Client service panel. */
  CLIENT_SERVICE: [],
  /* A meeting's record is its minutes, which are held on the meeting itself. */
  MEETING: [],
};

export const EVIDENCE_LABELS: Record<WorkItemEvidenceKind, string> = {
  DOCUMENT: "Document",
  SCREENSHOT: "Screenshot",
  QUERIES: "Queries run",
};

/**
 * The same kinds as a plain phrase for a sentence about what is still owed — "It still needs a
 * document", "queries and a screenshot" — rather than as a panel label. Matches the server's own
 * wording in {@code WorkItemService#describe}, so the refusal reads the same wherever it appears.
 */
export const EVIDENCE_PHRASES: Record<WorkItemEvidenceKind, string> = {
  DOCUMENT: "a document",
  SCREENSHOT: "a screenshot",
  QUERIES: "queries",
};

/**
 * As much of a work item as it takes to say what it still owes.
 *
 * <p>Narrower than `WorkItemDetail`, which satisfies it structurally, so that the status drawer
 * — which deliberately holds only enough of an item to move one — can ask the same question the
 * evidence panel asks, from the same function. Two places deciding separately what "still
 * outstanding" means is how the panel and the Complete button come to disagree.
 */
export interface EvidenceBearing {
  workItemType: WorkItemType;
  evidence: { kind: WorkItemEvidenceKind; attachmentId?: string | null }[];
  migrationDetail?: { queries?: string | null } | null;
}

/**
 * What is still outstanding before this item can be completed.
 *
 * <p>A migration's queries are not evidence like the rest — they live on `migrationDetail`
 * rather than as an attached file (§15), so "held" is checked in two different places depending
 * on the kind. Blank queries do not count as held: a migration detail record can exist with
 * nothing typed into it yet.
 *
 * <p>"Nothing is owed" is an empty requirement list, not a missing one. Keyed off key absence,
 * a type nobody had added an entry for looked identical to a type that genuinely owes nothing.
 *
 * <p>A row whose file has been deleted is not held. The row survives the deletion, but the
 * server counts live files, so counting rows here would have this screen say everything was in
 * hand about an item the API would go on refusing.
 */
export function outstandingEvidence(item: EvidenceBearing): WorkItemEvidenceKind[] {
  const required = TYPE_EVIDENCE[item.workItemType];
  if (required.length === 0) {
    return [];
  }
  const held = new Set<WorkItemEvidenceKind>(
    item.evidence.filter((row) => row.attachmentId != null).map((row) => row.kind),
  );
  if (item.migrationDetail?.queries?.trim()) {
    held.add("QUERIES");
  }
  return required.filter((kind) => !held.has(kind));
}

/** The six types that go through code review and QA — everything else is a simple workflow. */
const TESTING_TYPES: ReadonlySet<WorkItemType> = new Set<WorkItemType>([
  "EPIC",
  "USER_STORY",
  "TECHNICAL_TASK",
  "BUG",
  "SPIKE",
  "SUBTASK",
]);

/** True for the types that go through review and testing — the only ones with two phases. */
export function hasTestingPhase(type: WorkItemType): boolean {
  return TESTING_TYPES.has(type);
}

export interface PhaseLine {
  text: string;
  tone: "calm" | "good" | "late";
}

/** Whole calendar days between two `YYYY-MM-DD` dates, `to` minus `from`. */
function daysBetween(from: string, to: string): number {
  const fromMs = Date.parse(`${from}T00:00:00Z`);
  const toMs = Date.parse(`${to}T00:00:00Z`);
  return Math.round((toMs - fromMs) / (24 * 60 * 60 * 1000));
}

function pluralDays(days: number): string {
  return `${days} day${days === 1 ? "" : "s"}`;
}

/**
 * "Due 23 Sep · done 22 Sep, on time" — how a phase reads on the item page.
 *
 * <p>`done` null means still in progress; a past `due` with no `done` reads as overdue.
 */
export function describePhase(
  due: string | null | undefined,
  done: string | null | undefined,
  today: string,
): PhaseLine {
  if (!due) {
    return { text: "No due date", tone: "calm" };
  }
  const dueText = shortDate(due);
  if (done) {
    const doneText = shortDate(done);
    if (done <= due) {
      return { text: `Due ${dueText} · done ${doneText}, on time`, tone: "good" };
    }
    const daysLate = daysBetween(due, done);
    return {
      text: `Due ${dueText} · done ${doneText}, late by ${pluralDays(daysLate)}`,
      tone: "late",
    };
  }
  if (today > due) {
    const daysOverdue = daysBetween(due, today);
    return { text: `Due ${dueText} · overdue by ${pluralDays(daysOverdue)}`, tone: "late" };
  }
  return { text: `Due ${dueText} · in progress`, tone: "calm" };
}

/** What Start testing still needs, in the server's words. */
export function missingForTesting(item: {
  testerId?: string | null;
  testingDueDate?: string | null;
}): string[] {
  const missing: string[] = [];
  if (!item.testerId) {
    missing.push("a tester");
  }
  if (!item.testingDueDate) {
    missing.push("a testing due date");
  }
  return missing;
}

export const STATUS_LABELS: Record<WorkItemStatus, string> = {
  BACKLOG: "Backlog",
  SELECTED: "Selected",
  IN_PROGRESS: "In progress",
  DEV_DONE: "Development done",
  /* Retired: review is no longer a column. Kept so history written while it was still reads. */
  CODE_REVIEW: "Code review",
  READY_FOR_QA: "Ready for testing",
  QA: "QA",
  DONE: "Done",
  CANCELLED: "Cancelled",
};

/**
 * What a late item is still waiting on, when its deadline was measured at development.
 *
 * <p>Reports judge a developer's deadline by the end of development, so an item can be counted
 * late and still be sitting in review or testing. The number is frozen at that point — it does
 * not grow while QA holds it — but a line that only says "1d late" reads as work still slipping,
 * which is the opposite of what happened: somebody finished, a day over, and handed it on.
 *
 * <p>Null once there is nothing left to wait for, and for anything still in development, where
 * "late" means exactly what it says.
 */
export function awaitingAfterDevelopment(status: string | null | undefined): string | null {
  switch (status) {
    case "DEV_DONE":
      return "awaiting review and testing";
    case "CODE_REVIEW":
      return "awaiting review";
    case "READY_FOR_QA":
      return "awaiting testing";
    case "QA":
      return "in testing";
    default:
      return null;
  }
}

/**
 * A recorded history value, in the words the rest of the interface uses.
 *
 * <p>History stores what the server stored — `READY_FOR_QA`, not the name anybody reads. Left raw,
 * the activity feed is the one place still calling a status by its old name, which is exactly
 * where somebody goes to work out what happened.
 */
export function historyValue(eventType: HistoryEventType, value: string): string {
  if (eventType !== "STATUS_CHANGED") {
    return value;
  }
  return STATUS_LABELS[value as WorkItemStatus] ?? value;
}

export const STATUS_TONE: Record<WorkItemStatus, Tone> = {
  BACKLOG: "gray",
  SELECTED: "gray",
  IN_PROGRESS: "blue",
  DEV_DONE: "violet",
  CODE_REVIEW: "violet",
  READY_FOR_QA: "amber",
  QA: "amber",
  DONE: "green",
  CANCELLED: "gray",
};

/** §51's board order, so status pickers read as a progression rather than an alphabet. */
export const STATUS_ORDER: WorkItemStatus[] = [
  "BACKLOG",
  "SELECTED",
  "IN_PROGRESS",
  "DEV_DONE",
  "READY_FOR_QA",
  "QA",
  "DONE",
  "CANCELLED",
];

/**
 * Whether an item has stopped for good, one way or the other.
 *
 * <p>The same question the server's {@code WorkItemStatus.isClosed} answers, and it has to be
 * the same answer. Treating cancelled work as still open puts it in front of people looking for
 * something to do, and offers them actions — claiming it, above all — that lead nowhere.
 */
export function isClosedStatus(status: WorkItemStatus): boolean {
  return status === "DONE" || status === "CANCELLED";
}

/**
 * §59's coded reasons, in the order the drawer offers them.
 *
 * <p>Ordered by how often they are the real answer rather than alphabetically, so the common case
 * is near the top and nobody reaches for "Something else" because the right code was buried.
 *
 * <p>"Moved here by mistake" leads deliberately. Undoing a misclick is the most common reason
 * anybody moves work backwards, and without it near the top people pick whichever code is closest
 * — recording a review that never asked for changes, or testing that never failed.
 */
export const TRANSITION_REASONS: [WorkItemTransitionReason, string][] = [
  ["MOVED_IN_ERROR", "Moved here by mistake"],
  ["ACCEPTANCE_CRITERIA_NOT_MET", "Acceptance criteria not met"],
  ["DEFECT_FOUND", "A defect was found"],
  ["QA_FAILED", "Testing failed"],
  ["REVIEWED_AFTER_TESTING", "The review landed after testing"],
  ["REVIEW_CHANGES_REQUESTED", "The review asked for changes"],
  ["REQUIREMENT_CHANGED", "The requirement changed"],
  ["INCOMPLETE_WORK", "The work was not finished"],
  ["BLOCKED_BY_DEPENDENCY", "Blocked by something else"],
  ["DEPRIORITISED", "Deprioritised"],
  ["OUT_OF_SCOPE", "Out of scope"],
  ["DUPLICATE", "Duplicate of other work"],
  ["SUPERSEDED", "Superseded by a different approach"],
  ["NOT_REPRODUCIBLE", "Could not be reproduced"],
  ["RAISED_IN_ERROR", "Raised in error"],
  ["CLOSED_IN_ERROR", "Closed in error"],
  ["OTHER", "Something else"],
];

/** Mirrors WorkItemTransitionReason.requiresComment() — the server refuses these without one. */
export const REASON_REQUIRING_COMMENT: WorkItemTransitionReason[] = ["OTHER"];

/**
 * Worst first, so a priority picker reads as a scale rather than an alphabet.
 *
 * <p>Here rather than in each screen that offers the choice: the same four values were written
 * out separately in the search page, the sprint bulk drawer and the project work tab, which is
 * three places for them to disagree the day a fifth is added.
 */
export const PRIORITY_ORDER: Priority[] = ["CRITICAL", "HIGH", "MEDIUM", "LOW"];

/** Which way a client service call went, in the order the form offers it. */
export const CONTACT_DIRECTIONS: [ClientContactDirection, string][] = [
  ["THEY_CALLED_US", "They called us"],
  ["WE_CALLED_THEM", "We called them"],
];

/** How the client was reached, commonest first rather than alphabetically. */
export const CONTACT_CHANNELS: [ClientContactChannel, string][] = [
  ["PHONE", "Phone"],
  ["VIDEO_CALL", "Video call"],
  ["EMAIL", "Email"],
  ["CHAT", "Chat or message"],
  ["IN_PERSON", "In person"],
  ["OTHER", "Something else"],
];

/** The label for a stored direction or channel, for the panels that only read them. */
export const CONTACT_DIRECTION_LABELS = Object.fromEntries(CONTACT_DIRECTIONS) as Record<
  ClientContactDirection,
  string
>;

export const CONTACT_CHANNEL_LABELS = Object.fromEntries(CONTACT_CHANNELS) as Record<
  ClientContactChannel,
  string
>;

export const PRIORITY_LABELS: Record<Priority, string> = {
  CRITICAL: "Critical",
  HIGH: "High",
  MEDIUM: "Medium",
  LOW: "Low",
};

export const PRIORITY_TONE: Record<Priority, Tone> = {
  CRITICAL: "red",
  HIGH: "amber",
  MEDIUM: "gray",
  LOW: "gray",
};

export const SEVERITY_LABELS: Record<BugSeverity, string> = {
  BLOCKER: "Blocker",
  CRITICAL: "Critical",
  MAJOR: "Major",
  MINOR: "Minor",
  TRIVIAL: "Trivial",
};

export const SEVERITY_TONE: Record<BugSeverity, Tone> = {
  BLOCKER: "red",
  CRITICAL: "red",
  MAJOR: "amber",
  MINOR: "gray",
  TRIVIAL: "gray",
};

export const BUG_SOURCE_LABELS: Record<BugSource, string> = {
  DEVELOPMENT: "Development",
  QA: "QA",
  UAT: "UAT",
  PRODUCTION: "Production",
  CUSTOMER: "Customer",
  INTERNAL_TESTING: "Internal testing",
};

/** The seven §85 names, as a person would pick them. */
export const LINK_CHOICES: { value: string; label: string }[] = [
  { value: "BLOCKS", label: "blocks" },
  { value: "IS_BLOCKED_BY", label: "is blocked by" },
  { value: "RELATES_TO", label: "relates to" },
  { value: "DUPLICATES", label: "duplicates" },
  { value: "IS_DUPLICATED_BY", label: "is duplicated by" },
  { value: "CAUSES", label: "causes" },
  { value: "IS_CAUSED_BY", label: "is caused by" },
];

/**
 * What a history entry says, in a sentence.
 *
 * <p>The raw event names are for the database. "STATUS_CHANGED" tells a reader nothing they could
 * not already see; "moved to" followed by the two values is the thing they came to find out.
 */
export const EVENT_PHRASING: Record<HistoryEventType, string> = {
  WORK_ITEM_CREATED: "created this",
  TITLE_CHANGED: "renamed it",
  DESCRIPTION_CHANGED: "edited the description",
  STATUS_CHANGED: "moved it",
  PRIORITY_CHANGED: "changed the priority",
  ASSIGNEE_CHANGED: "reassigned it",
  CONTRIBUTOR_ADDED: "added a contributor",
  CONTRIBUTOR_REMOVED: "removed a contributor",
  TEAM_CHANGED: "moved it to another team",
  SPRINT_CHANGED: "moved it to another sprint",
  EPIC_CHANGED: "moved it to another epic",
  PARENT_CHANGED: "moved it in the hierarchy",
  ESTIMATE_CHANGED: "re-estimated it",
  START_DATE_CHANGED: "moved the start date",
  DUE_DATE_CHANGED: "moved the due date",
  FORECAST_CHANGED: "changed the forecast",
  LABELS_CHANGED: "changed the labels",
  WORKLOG_ADDED: "logged time",
  WORKLOG_UPDATED: "changed a worklog",
  WORKLOG_DELETED: "removed a worklog",
  COMMENT_ADDED: "commented",
  COMMENT_EDITED: "edited a comment",
  COMMENT_REMOVED: "withdrew a comment",
  ATTACHMENT_ADDED: "attached a file",
  ATTACHMENT_REMOVED: "removed an attachment",
  BLOCKER_ADDED: "raised a blocker",
  BLOCKER_RESOLVED: "cleared a blocker",
  DEPENDENCY_CHANGED: "changed a dependency",
  ARCHIVED: "filed this into a group",
  UNARCHIVED: "took this back out of a group",
};

/**
 * What an entry says, when the event alone does not say enough.
 *
 * <p>One event type covers three different figures. "re-estimated it from 10h to 2h" and
 * "re-estimated it from 15h to 20h" are the remaining hours and the whole job's size, and read
 * as contradictions of each other when the feed will not say which is which. The same applies to
 * the dates, where committed and forecast answer different questions (§56).
 */
const FIELD_PHRASING: Record<string, string> = {
  originalEstimate: "set the original estimate at",
  currentEstimate: "re-estimated the whole job",
  remainingEstimate: "changed what is left",
  committedStartDate: "moved the committed start",
  committedDueDate: "moved the committed due date",
  forecastStartDate: "changed the forecast start",
  forecastDueDate: "changed the forecast due date",
};

/**
 * How one history entry reads, given both what happened and which field it happened to.
 *
 * <p>Falls back to the event's own phrasing whenever the field adds nothing — which is most of
 * them, since a title change has only one thing it could be about.
 */
export function phraseHistoryEntry(
  eventType: HistoryEventType,
  fieldName: string | null,
): string {
  return (fieldName && FIELD_PHRASING[fieldName]) || EVENT_PHRASING[eventType];
}

/**
 * Events recorded as having happened, without the values they changed.
 *
 * <p>A description edit carries no bodies of text on purpose: a timeline with a thousand-word
 * description pasted into it twice makes every other entry unfindable, and the item itself holds
 * the current text. The entry says that it happened and who did it, which is what a timeline is
 * for.
 *
 * <p>An event may only appear here if its phrasing stands alone. "edited the description" is a
 * whole sentence; "moved it" is not, and hiding a status change's two values would leave a line
 * that says nothing.
 */
const WITHOUT_VALUES: Set<HistoryEventType> = new Set(["DESCRIPTION_CHANGED"]);

/**
 * Whether a history entry's old and new values are worth rendering.
 *
 * <p>Without this the generic "from X to Y" clause is appended to every entry that has both, so
 * the placeholders standing in for the absent text were dressed up as real values: "edited the
 * description from (previous text) to (updated)" reads as though the description was set to the
 * word "(updated)".
 */
export function showsValues(eventType: HistoryEventType): boolean {
  return !WITHOUT_VALUES.has(eventType);
}

/**
 * Hours for display.
 *
 * <p>The server stores minutes so every sum is exact; these are read rather than summed, so one
 * decimal is enough.
 */
export function formatHours(hours: number | null | undefined): string {
  if (hours == null) {
    return "—";
  }
  const rounded = Math.round(hours * 10) / 10;
  return `${rounded.toLocaleString(undefined, { maximumFractionDigits: 1 })}h`;
}

/** A variance with its sign, because the sign is the information. */
export function formatVariance(hours: number | null | undefined): string {
  if (hours == null || hours === 0) {
    return "on estimate";
  }
  return hours > 0
    ? `+${formatHours(hours)}`
    : `−${formatHours(Math.abs(hours))}`;
}

/** Days late or early, phrased so nobody has to work out which direction is which. */
export function formatSlippage(days: number): string {
  if (days === 0) {
    return "on schedule";
  }
  return days > 0 ? `${days} days late` : `${Math.abs(days)} days early`;
}

/** How long a blocker has been in the way (§86). */
export function formatAge(days: number): string {
  if (days === 0) {
    return "raised today";
  }
  if (days === 1) {
    return "1 day";
  }
  return `${days} days`;
}

export function formatTimestamp(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * How long a finished task has been waiting for a reviewer, or null when it is not waiting.
 *
 * <p>Only Development done is waiting. An item in Code review has a reviewer looking at it, and
 * anything further on has been reviewed — saying "waiting" about either would be false.
 */
export function waitingForReviewDays(
  item: { status: WorkItemStatus; devCompletedDate?: string | null },
  today: string,
): number | null {
  if (item.status !== "DEV_DONE" || !item.devCompletedDate) {
    return null;
  }
  const from = Date.parse(`${item.devCompletedDate}T00:00:00`);
  const to = Date.parse(`${today}T00:00:00`);
  if (Number.isNaN(from) || Number.isNaN(to)) {
    return null;
  }
  return Math.max(0, Math.round((to - from) / 86_400_000));
}

/** Three days is where waiting stops being ordinary and starts being worth chasing. */
export const REVIEW_WAIT_IS_LONG = 3;

/** The review, in the words the screens use. */
export const REVIEW_LABELS: Record<ReviewState, string> = {
  NOT_REVIEWED: "Not reviewed",
  IN_REVIEW: "With a reviewer",
  CHANGES_REQUESTED: "Changes requested",
  APPROVED: "Reviewed",
};

export const REVIEW_TONE: Record<ReviewState, Tone> = {
  NOT_REVIEWED: "amber",
  IN_REVIEW: "violet",
  CHANGES_REQUESTED: "red",
  APPROVED: "green",
};

/**
 * Whether this item's code is still waiting to be read.
 *
 * <p>Only development work has code, and only work that has been finished can be reviewed —
 * flagging something nobody has written yet would be noise on every item in the backlog.
 */
export function needsReview(item: {
  workItemType: WorkItemType;
  reviewState?: ReviewState;
  devCompletedDate?: string | null;
}): boolean {
  return (
    hasTestingPhase(item.workItemType)
    && item.devCompletedDate != null
    && item.reviewState !== "APPROVED"
  );
}

/**
 * Whether the testing this passed no longer describes the code that was approved.
 *
 * <p>Testing may run ahead of a review, and finishing no longer waits for one. What does not
 * follow is that the two are interchangeable: a tester who passed unreviewed code tested whatever
 * was written, not whatever the reviewer went on to accept. Where a reviewer has since approved
 * it, the pass is out of date and the work owes another round.
 *
 * <p>The server settles this at the moment of passing, because a review approved the same
 * afternoon as the testing is the ordinary case and two dates cannot say which came first. This
 * only reads the answer.
 */
export function needsRetest(item: { needsRetestAfterReview?: boolean }): boolean {
  return item.needsRetestAfterReview === true;
}
